import type { ConnectionState } from '../lib/connectivity';
import type { LocalSale } from './types';

/**
 * Sendestatus eines Verkaufs in Klartext (U17e): als Chip in der Karte, im Blatt „Letzter Verkauf“ und im Namen des
 * Symbols ↶. Rein: Die Eingabe ist der Verkauf und der Zustand der Verbindung (`pos.connection`, U15a) samt Ergebnis des
 * letzten Laufs. Fünf Zustände.
 */

export type SendKind = 'sent' | 'sending' | 'waiting' | 'retry' | 'rejected';

export interface SendState {
  kind: SendKind;
  /** Kurzer Text im Chip („Gesendet ✓ 14:31“). */
  chip: string;
  /** Erklärung beim Aufklappen. */
  explain: string;
  tone: 'ok' | 'info' | 'warn' | 'error';
  /** „Jetzt senden“ (nur bei Fehler im letzten Lauf). */
  canSendNow: boolean;
  /** Ein erneutes Senden hilft nie: Die Kennung ist am Server schon belegt (nur „Nicht übernommen“). */
  retryUseless: boolean;
}

export interface SendContext {
  connection: ConnectionState;
  /** Der letzte Sync-Lauf endete mit einem Fehler (`status.state === 'error'`). */
  syncError: boolean;
  /**
   * Stornos, die der Server noch nicht bestätigt hat, je Verkauf (`PosStore.openVoids`). Ein gesendeter Verkauf mit offenem
   * Storno ist nicht „gesendet“: Der Chip zeigt dann den Stand des Stornos.
   */
  openVoids?: ReadonlyMap<string, 'pending' | 'failed'> | undefined;
}

const TIME = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});

export function sendState(sale: LocalSale, context: SendContext): SendState {
  if (sale.state === 'failed') {
    return {
      kind: 'rejected',
      chip: 'Nicht übernommen',
      explain:
        'Dieser Verkauf wurde nicht übernommen. Er ist gesichert, die Basar-Leitung sieht ihn.',
      tone: 'error',
      canSendNow: false,
      retryUseless: true,
    };
  }
  const storno = sale.state === 'synced' ? context.openVoids?.get(sale.clientUuid) : undefined;
  if (storno === 'failed') {
    return {
      kind: 'rejected',
      chip: 'Storno nicht übernommen',
      explain:
        'Das Storno wurde nicht übernommen. Es ist gesichert, die Basar-Leitung sieht es. Der Verkauf selbst ist gesendet.',
      tone: 'error',
      canSendNow: false,
      retryUseless: true,
    };
  }
  if (sale.state === 'synced' && storno === undefined) {
    return {
      kind: 'sent',
      chip:
        sale.syncedAtMs === undefined ? 'Gesendet ✓' : `Gesendet ✓ ${TIME.format(sale.syncedAtMs)}`,
      explain: 'Die Basar-Leitung sieht diesen Verkauf.',
      tone: 'ok',
      canSendNow: false,
      retryUseless: false,
    };
  }
  // Ab hier wartet etwas: der Verkauf selbst oder (bei gesendetem Verkauf) sein Storno
  if (context.connection !== 'online') {
    return {
      kind: 'waiting',
      chip: storno === undefined ? 'Wartet auf Netz' : 'Storno wartet auf Netz',
      explain:
        storno === undefined
          ? 'Noch auf diesem Gerät gespeichert, wird automatisch gesendet, sobald Netz da ist. Nichts geht verloren.'
          : 'Der Verkauf ist gesendet, das Storno ist noch auf diesem Gerät gespeichert und wird automatisch gesendet, sobald Netz da ist.',
      tone: 'warn',
      canSendNow: false,
      retryUseless: false,
    };
  }
  if (context.syncError) {
    return {
      kind: 'retry',
      chip: storno === undefined ? 'Fehler, wird wiederholt' : 'Storno: Fehler, wird wiederholt',
      explain: 'Senden hat nicht geklappt, die Kasse versucht es weiter.',
      tone: 'warn',
      canSendNow: true,
      retryUseless: false,
    };
  }
  return {
    kind: 'sending',
    chip: storno === undefined ? 'Wird gesendet …' : 'Storno wird gesendet …',
    explain: 'Geht in den nächsten Sekunden hinaus.',
    tone: 'info',
    canSendNow: false,
    retryUseless: false,
  };
}
